<div class="h-full w-full flex flex-col bg-body-background relative overflow-x-hidden"
     [ngClass]="hideHeader ? 'p-0 gap-4' : 'p-6 lg:p-10 pb-16 gap-8'">

  @if (isLoading()) {
  <div class="flex items-center justify-center p-20 w-full">
    <i class="fas fa-circle-notch fa-spin text-5xl text-primary"></i>
  </div>
  } @else if (isProcessing()) {

  <div
       class="flex flex-col items-center justify-center p-12 text-center animate-fade-in-up w-full h-full min-h-[400px]">
    <div class="relative w-24 h-24 flex items-center justify-center mb-8">
      <div class="absolute inset-0 border-4 border-secondary-border rounded-full"></div>
      <div class="absolute inset-0 border-4 border-primary rounded-full border-t-transparent animate-spin"></div>
      <i class="fas fa-file-import text-3xl text-primary animate-pulse"></i>
    </div>
    <h2 class="text-2xl font-bold text-heading mb-3">{{ config.texts.processingTitle }}</h2>
    <p class="text-secondary-text max-w-md">{{ config.texts.processingDesc }}</p>
  </div>

  } @else if (templateData(); as template) {

  @if (!hideActions && !hideBackButton) {
  <button (click)="goBack()"
          class="flex items-center gap-2 text-secondary-text hover:text-primary transition-colors focus:outline-none w-max">
    <i class="fas fa-arrow-left"></i>
    <span class="font-medium text-sm">Back to Templates</span>
  </button>
  }

  @if (!hideHeader) {
  <header class="bg-component-inner border border-primary-border rounded-xl p-8 shadow-sm flex flex-col gap-4">
    <div class="flex justify-between items-start flex-wrap gap-6">
      <div class="flex flex-col flex-1 min-w-[300px]">
        <h1 class="text-3xl font-extrabold text-heading tracking-tight mb-2">{{ template.name }}</h1>
        <p class="text-secondary-text text-base max-w-3xl">{{ template.description }}</p>
      </div>

      <div class="flex flex-col gap-4 items-end shrink-0">
        <div
             class="bg-alt-bold-background border border-secondary-border px-3 py-1.5 rounded text-xs font-mono text-secondary-text">
          <span class="font-semibold text-primary-text mr-1">Key:</span> {{ template.key }}
        </div>

        @if (!hideActions && !hideIntegrateButton) {
        <button (click)="startIntegrationFlow()"
                class="bg-primary hover:bg-hover-primary text-primary-button-text font-semibold rounded-lg px-6 py-3 transition-colors shadow-default flex items-center gap-2">
          <i class="fas fa-plus-circle"></i>
          {{ config.texts.addTemplateBtn }}
        </button>
        }
      </div>
    </div>

    <div class="flex flex-wrap gap-2 mt-2">
      @for (tag of template.tags; track tag) {
      <nz-tag class="!m-0 text-sm bg-section-background border-secondary-border text-primary-text">{{ tag }}</nz-tag>
      }
    </div>

    <div class="mt-4 pt-4 border-t border-secondary-border flex flex-wrap gap-6 text-sm text-secondary-text">
      <span><i class="fas fa-calendar-alt mr-2"></i>{{ template.createdDate | date:'mediumDate' }}</span>
      <span><i class="fas fa-user mr-2"></i>{{ template.createdBy || 'System' }}</span>
    </div>
  </header>

  @if (template.value?.coverPage) {
  <section class="bg-component-inner border border-primary-border rounded-xl p-8 shadow-sm flex flex-col gap-4">
    <h2 class="text-2xl font-bold text-heading border-b border-secondary-border pb-4"><i
         class="fad fa-book-open mr-2 text-primary"></i>{{ config.texts.previewCoverPageTitle }}</h2>
    <div class="markdown-normalize text-primary-text max-w-none mt-2">
      <rapider-markdown-preview [data]="template.value.coverPage"></rapider-markdown-preview>
    </div>
  </section>
  }
  }

  <section class="bg-component-inner border border-primary-border rounded-xl p-6 shadow-sm flex flex-col gap-4">
    <h2 class="text-xl font-bold text-heading border-b border-secondary-border pb-3"><i
         class="fad fa-list-ul mr-2 text-primary"></i>{{ config.texts.previewModelsSummaryTitle }}</h2>
    <div class="flex flex-col gap-2">
      @for (model of modelsList(); track model.name) {
      <div (click)="scrollToModel(model.name)"
           class="flex flex-col p-4 rounded-lg bg-alt-bold-background hover:bg-hover-default cursor-pointer transition-colors border border-secondary-border hover:border-primary group">
        <span class="font-bold text-primary-text group-hover:text-primary transition-colors text-lg mb-1">
          {{ model.title }} <span class="text-xs text-secondary-text font-mono ml-1 font-normal">({{ model.datatableName
            || model.name }})</span>
        </span>
        <span class="text-sm text-secondary-text line-clamp-2">{{ model.description }}</span>
      </div>
      }
    </div>
  </section>

  <section class="flex flex-col gap-8 mt-4">
    <h2 class="text-2xl font-bold text-heading px-2"><i class="fad fa-database mr-2 text-primary"></i>{{
      config.texts.previewModelsTitle }}</h2>

    @for (model of modelsList(); track model.name) {
    <div [id]="'model-card-' + model.name"
         class="bg-component-inner border border-primary-border rounded-xl shadow-sm overflow-hidden flex flex-col scroll-mt-24">

      <div class="p-6 border-b border-secondary-border bg-alt-background flex justify-between items-start cursor-pointer hover:bg-hover-default transition-colors"
           (click)="toggleModelExpansion(model.name)">
        <div class="flex flex-col">
          <h3 class="text-xl font-bold text-primary-text mb-1">{{ model.title }} <span
                  class="text-sm font-mono font-normal text-secondary-text ml-2">({{ model.datatableName || model.name
              }})</span></h3>
          <p class="text-secondary-text text-sm">{{ model.description }}</p>
          @if (model.domainName) {
          <div class="mt-3">
            <nz-tag [nzColor]="'blue'"
                    class="!m-0 text-xs">{{ model.domainName }}</nz-tag>
          </div>
          }
        </div>

        <button
                class="text-secondary-text hover:text-primary-text transition-colors focus:outline-none p-2 ml-4 rounded-md hover:bg-component-inner shrink-0">
          <i class="fa-regular text-lg"
             [ngClass]="expandedModels()[model.name] ? 'fa-angles-up' : 'fa-angles-down'"></i>
        </button>
      </div>

      @if (expandedModels()[model.name]) {

      <div class="p-6 flex flex-col gap-4">
        <h4 class="text-lg font-bold text-heading">{{ config.texts.previewFieldsTitle }}</h4>
        <nz-table #fieldsTable
                  [nzData]="model.fields"
                  [nzShowPagination]="false"
                  nzSize="small"
                  class="border border-secondary-border rounded overflow-hidden">
          <thead>
            <tr class="bg-table-header-background">
              <th class="!bg-transparent text-primary-text">{{ config.texts.previewTableFieldName }}</th>
              <th class="!bg-transparent text-primary-text">{{ config.texts.previewTableFieldType }}</th>
              <th class="!bg-transparent text-primary-text">{{ config.texts.previewTableFieldFlags }}</th>
              <th class="!bg-transparent text-primary-text">{{ config.texts.previewTableFieldDesc }}</th>
            </tr>
          </thead>
          <tbody>
            @for (field of fieldsTable.data; track field.name) {
            <tr class="hover:bg-hover-table">
              <td
                  class="!bg-transparent border-t border-secondary-border font-mono text-sm font-semibold text-primary-text">
                {{ field.name }}</td>
              <td class="!bg-transparent border-t border-secondary-border">
                <nz-tag class="!m-0 text-xs bg-section-background border-secondary-border">{{ field.type }}{{
                  field.isArray ? '[]' : '' }}</nz-tag>
              </td>
              <td class="!bg-transparent border-t border-secondary-border flex gap-1 flex-wrap">
                @if (field.isPrimaryKey) { <nz-tag [nzColor]="'gold'"
                        class="!m-0 text-[10px]">PK</nz-tag> }
                @if (field.isRequired) { <nz-tag [nzColor]="'error'"
                        class="!m-0 text-[10px]">Required</nz-tag> }
                @if (field.isUnique) { <nz-tag [nzColor]="'cyan'"
                        class="!m-0 text-[10px]">Unique</nz-tag> }
                @if (field.isHidden) { <nz-tag
                        class="!m-0 text-[10px] text-secondary-text bg-alt-background">Hidden</nz-tag> }
              </td>
              <td class="!bg-transparent border-t border-secondary-border text-sm text-secondary-text">{{
                field.description }}</td>
            </tr>
            }
          </tbody>
        </nz-table>
      </div>

      @if (model.relations && model.relations.length > 0) {
      <div class="p-6 border-t border-secondary-border flex flex-col gap-4 bg-section-background">
        <h4 class="text-lg font-bold text-heading">{{ config.texts.previewRelationsTitle }}</h4>
        <nz-table #relationsTable
                  [nzData]="model.relations"
                  [nzShowPagination]="false"
                  nzSize="small"
                  class="border border-secondary-border rounded overflow-hidden">
          <thead>
            <tr class="bg-table-header-background">
              <th class="!bg-transparent text-primary-text">{{ config.texts.previewTableRelName }}</th>
              <th class="!bg-transparent text-primary-text">{{ config.texts.previewTableRelType }}</th>
              <th class="!bg-transparent text-primary-text">{{ config.texts.previewTableRelTarget }}</th>
              <th class="!bg-transparent text-primary-text">{{ config.texts.previewTableRelDesc }}</th>
            </tr>
          </thead>
          <tbody>
            @for (rel of relationsTable.data; track rel.name) {
            <tr class="hover:bg-hover-table">
              <td
                  class="!bg-transparent border-t border-secondary-border font-mono text-sm font-semibold text-primary-text">
                {{ rel.name }}</td>
              <td class="!bg-transparent border-t border-secondary-border">
                <nz-tag class="!m-0 text-xs bg-alt-bold-background border-secondary-border text-primary-text">{{
                  rel.type }}</nz-tag>
              </td>
              <td class="!bg-transparent border-t border-secondary-border text-sm font-bold text-primary cursor-pointer hover:underline transition-colors"
                  (click)="scrollToModel(rel.targetModel)">
                {{ rel.targetModel }}
              </td>
              <td class="!bg-transparent border-t border-secondary-border text-sm text-secondary-text">
                {{ rel.description }}
                @if (rel.throughModel) {
                <br><span class="text-xs italic">Through: <span
                        class="font-bold cursor-pointer hover:text-primary hover:underline transition-colors"
                        (click)="scrollToModel(rel.throughModel)">{{ rel.throughModel }}</span></span>
                }
              </td>
            </tr>
            }
          </tbody>
        </nz-table>
      </div>
      }

      @if (checkAiConfig(model.aiAgentConfig)) {
      <div class="p-6 border-t border-secondary-border flex flex-col gap-6 bg-alt-bold-background">
        <h4 class="text-lg font-bold text-heading flex items-center gap-2"><i class="fas fa-robot text-primary"></i>{{
          config.texts.previewAiConfigTitle }}</h4>

        <div class="flex flex-col gap-6">

          @if (model.aiAgentConfig.vectorSearchFields) {
          <div class="bg-component-inner border border-secondary-border rounded-lg p-6 shadow-sm flex flex-col gap-3">
            <h5 class="text-lg font-bold text-primary-text border-b border-secondary-border pb-3">{{
              config.texts.previewAiVectorSearch }}</h5>
            <div class="flex flex-wrap gap-2">
              @for (field of model.aiAgentConfig.vectorSearchFields; track field) {
              <nz-tag class="!m-0 text-sm bg-alt-background border-secondary-border text-primary-text px-3 py-1">{{
                field }}</nz-tag>
              }
            </div>
          </div>
          }

          @if (model.aiAgentConfig.planning?.availableOperations) {
          <div class="bg-component-inner border border-secondary-border rounded-lg p-6 shadow-sm flex flex-col gap-3">
            <h5 class="text-lg font-bold text-primary-text border-b border-secondary-border pb-3">{{
              config.texts.previewAiPlanning }}</h5>
            <div class="flex flex-wrap gap-2">
              @for (op of model.aiAgentConfig.planning.availableOperations; track op) {
              <nz-tag class="!m-0 text-sm bg-primary/10 border-primary/20 text-primary px-3 py-1">{{ op | uppercase
                }}</nz-tag>
              }
            </div>
          </div>
          }

          @if (model.aiAgentConfig.entityItemCreate; as createCfg) {
          <div class="bg-component-inner border border-secondary-border rounded-lg p-6 shadow-sm flex flex-col gap-6">
            <h5 class="text-lg font-bold text-primary-text border-b border-secondary-border pb-3">{{
              config.texts.previewAiCreate }}</h5>

            @if (createCfg.requiredFields) {
            <div class="flex flex-col gap-2">
              <span class="text-sm font-bold text-secondary-text uppercase tracking-wider">{{
                config.texts.previewAiRequiredFields }}</span>
              <div class="flex flex-wrap gap-2">
                @for (req of createCfg.requiredFields; track req) { <nz-tag
                        class="!m-0 text-sm py-0.5 px-3 bg-alt-background border-secondary-border text-primary-text">{{
                  req }}</nz-tag> }
              </div>
            </div>
            }

            @if (createCfg.immutableFields) {
            <div class="flex flex-col gap-2">
              <span class="text-sm font-bold text-secondary-text uppercase tracking-wider">{{
                config.texts.previewAiImmutableFields }}</span>
              <div class="flex flex-wrap gap-2">
                @for (f of createCfg.immutableFields; track f) { <nz-tag
                        class="!m-0 text-sm py-0.5 px-3 bg-alt-background border-secondary-border text-primary-text">{{
                  f }}</nz-tag> }
              </div>
            </div>
            }

            @if (createCfg.requiredRelations) {
            <div class="flex flex-col gap-2">
              <span class="text-sm font-bold text-secondary-text uppercase tracking-wider">{{
                config.texts.previewAiRequiredRelations }}</span>
              <div class="flex flex-wrap gap-2">
                @for (rel of createCfg.requiredRelations; track rel) { <nz-tag
                        class="!m-0 text-sm py-0.5 px-3 bg-alt-background border-secondary-border text-primary-text">{{
                  rel }}</nz-tag> }
              </div>
            </div>
            }

            @if (createCfg.autoGenerateFields) {
            <div class="flex flex-col gap-2">
              <span class="text-sm font-bold text-secondary-text uppercase tracking-wider">{{
                config.texts.previewAiAutoGenerate }}</span>
              <div class="flex flex-wrap gap-2">
                @for (f of createCfg.autoGenerateFields; track f) { <nz-tag
                        class="!m-0 text-sm py-0.5 px-3 bg-alt-background border-secondary-border text-primary-text">{{
                  f }}</nz-tag> }
              </div>
            </div>
            }

            @if (createCfg.requireUserConfirmation !== undefined) {
            <div class="flex flex-col gap-1">
              <span class="text-sm font-bold text-secondary-text uppercase tracking-wider">{{
                config.texts.previewAiRequireUserConf }}</span>
              <span class="text-base font-medium text-primary-text">{{ createCfg.requireUserConfirmation ? 'Yes' : 'No'
                }}</span>
            </div>
            }

            @if (createCfg.customValidationPrompt) {
            <div class="flex flex-col gap-2">
              <span class="text-sm font-bold text-secondary-text uppercase tracking-wider">{{
                config.texts.previewAiCustomValidation }}</span>
              <span class="text-sm text-primary-text bg-alt-background p-3 rounded border border-secondary-border">{{
                createCfg.customValidationPrompt }}</span>
            </div>
            }

            @if (createCfg.additionalPrompt) {
            <div class="flex flex-col gap-3 mt-2 pt-4 border-t border-secondary-border">
              <span class="text-sm font-bold text-secondary-text uppercase tracking-wider">{{
                config.texts.previewAiPrompt }}</span>
              <div
                   class="markdown-normalize bg-alt-background p-5 rounded-lg border border-secondary-border text-primary-text w-full">
                <rapider-markdown-preview [data]="createCfg.additionalPrompt"></rapider-markdown-preview>
              </div>
            </div>
            }
          </div>
          }

          @if (model.aiAgentConfig.entityItemUpdate; as updateCfg) {
          <div class="bg-component-inner border border-secondary-border rounded-lg p-6 shadow-sm flex flex-col gap-6">
            <h5 class="text-lg font-bold text-primary-text border-b border-secondary-border pb-3">{{
              config.texts.previewAiUpdate }}</h5>

            @if (updateCfg.immutableFields) {
            <div class="flex flex-col gap-2">
              <span class="text-sm font-bold text-secondary-text uppercase tracking-wider">{{
                config.texts.previewAiImmutableFields }}</span>
              <div class="flex flex-wrap gap-2">
                @for (f of updateCfg.immutableFields; track f) { <nz-tag
                        class="!m-0 text-sm py-0.5 px-3 bg-alt-background border-secondary-border text-primary-text">{{
                  f }}</nz-tag> }
              </div>
            </div>
            }

            @if (updateCfg.autoGenerateFields) {
            <div class="flex flex-col gap-2">
              <span class="text-sm font-bold text-secondary-text uppercase tracking-wider">{{
                config.texts.previewAiAutoGenerate }}</span>
              <div class="flex flex-wrap gap-2">
                @for (f of updateCfg.autoGenerateFields; track f) { <nz-tag
                        class="!m-0 text-sm py-0.5 px-3 bg-alt-background border-secondary-border text-primary-text">{{
                  f }}</nz-tag> }
              </div>
            </div>
            }

            @if (updateCfg.requireUserConfirmation !== undefined) {
            <div class="flex flex-col gap-1">
              <span class="text-sm font-bold text-secondary-text uppercase tracking-wider">{{
                config.texts.previewAiRequireUserConf }}</span>
              <span class="text-base font-medium text-primary-text">{{ updateCfg.requireUserConfirmation ? 'Yes' : 'No'
                }}</span>
            </div>
            }

            @if (updateCfg.customValidationPrompt) {
            <div class="flex flex-col gap-2">
              <span class="text-sm font-bold text-secondary-text uppercase tracking-wider">{{
                config.texts.previewAiCustomValidation }}</span>
              <span class="text-sm text-primary-text bg-alt-background p-3 rounded border border-secondary-border">{{
                updateCfg.customValidationPrompt }}</span>
            </div>
            }

            @if (updateCfg.restrictedStates) {
            <div class="flex flex-col gap-2 mt-2">
              <span class="text-sm font-bold text-secondary-text uppercase tracking-wider">{{
                config.texts.previewAiRestrictedStates }}</span>
              <div class="flex flex-col gap-3">
                @for (state of updateCfg.restrictedStates; track state.description) {
                <div class="bg-alt-background border border-secondary-border p-4 rounded-lg flex flex-col gap-2">
                  <span class="text-sm font-medium text-primary-text">{{ state.description }}</span>
                  <span
                        class="text-xs font-mono text-secondary-text bg-component-inner p-2 rounded border border-primary-border">{{
                    state.where | json }}</span>
                </div>
                }
              </div>
            </div>
            }

            @if (updateCfg.additionalPrompt) {
            <div class="flex flex-col gap-3 mt-2 pt-4 border-t border-secondary-border">
              <span class="text-sm font-bold text-secondary-text uppercase tracking-wider">{{
                config.texts.previewAiPrompt }}</span>
              <div
                   class="markdown-normalize bg-alt-background p-5 rounded-lg border border-secondary-border text-primary-text w-full">
                <rapider-markdown-preview [data]="updateCfg.additionalPrompt"></rapider-markdown-preview>
              </div>
            </div>
            }
          </div>
          }

          @if (model.aiAgentConfig.entityRead; as readCfg) {
          <div class="bg-component-inner border border-secondary-border rounded-lg p-6 shadow-sm flex flex-col gap-6">
            <h5 class="text-lg font-bold text-primary-text border-b border-secondary-border pb-3">{{
              config.texts.previewAiRead }}</h5>

            <div class="flex gap-10 flex-wrap">
              @if (readCfg.defaultPageSize) {
              <div class="flex flex-col gap-1">
                <span class="text-sm font-bold text-secondary-text uppercase tracking-wider">{{
                  config.texts.previewAiPageSize }}</span>
                <span class="text-base font-mono font-bold text-primary-text">{{ readCfg.defaultPageSize }}</span>
              </div>
              }

              @if (readCfg.defaultContextLimit) {
              <div class="flex flex-col gap-1">
                <span class="text-sm font-bold text-secondary-text uppercase tracking-wider">{{
                  config.texts.previewAiContextLimit }}</span>
                <span class="text-base font-mono font-bold text-primary-text">{{ readCfg.defaultContextLimit }}</span>
              </div>
              }
            </div>

            @if (readCfg.hiddenFields) {
            <div class="flex flex-col gap-2">
              <span class="text-sm font-bold text-secondary-text uppercase tracking-wider">{{
                config.texts.previewAiHiddenFields }}</span>
              <div class="flex flex-wrap gap-2">
                @for (f of readCfg.hiddenFields; track f) { <nz-tag
                        class="!m-0 text-sm py-0.5 px-3 bg-alt-background border-secondary-border text-primary-text">{{
                  f }}</nz-tag> }
              </div>
            </div>
            }

            @if (readCfg.maskedFields) {
            <div class="flex flex-col gap-2">
              <span class="text-sm font-bold text-secondary-text uppercase tracking-wider">{{
                config.texts.previewAiMaskedFields }}</span>
              <div class="flex flex-wrap gap-2">
                @for (f of readCfg.maskedFields; track f) { <nz-tag
                        class="!m-0 text-sm py-0.5 px-3 bg-alt-background border-secondary-border text-primary-text">{{
                  f }}</nz-tag> }
              </div>
            </div>
            }

            @if (readCfg.additionalPrompt) {
            <div class="flex flex-col gap-3 mt-2 pt-4 border-t border-secondary-border">
              <span class="text-sm font-bold text-secondary-text uppercase tracking-wider">{{
                config.texts.previewAiPrompt }}</span>
              <div
                   class="markdown-normalize bg-alt-background p-5 rounded-lg border border-secondary-border text-primary-text w-full">
                <rapider-markdown-preview [data]="readCfg.additionalPrompt"></rapider-markdown-preview>
              </div>
            </div>
            }
          </div>
          }

          @if (model.aiAgentConfig.entityItemDelete; as deleteCfg) {
          <div class="bg-component-inner border border-secondary-border rounded-lg p-6 shadow-sm flex flex-col gap-6">
            <h5 class="text-lg font-bold text-primary-text border-b border-secondary-border pb-3">{{
              config.texts.previewAiDelete }}</h5>

            @if (deleteCfg.requireCaptchaConfirmation !== undefined) {
            <div class="flex flex-col gap-1">
              <span class="text-sm font-bold text-secondary-text uppercase tracking-wider">{{
                config.texts.previewAiCaptchaConf }}</span>
              <span class="text-base font-medium text-primary-text">{{ deleteCfg.requireCaptchaConfirmation ? 'Yes' :
                'No' }}</span>
            </div>
            }

            @if (deleteCfg.cascadingRelations) {
            <div class="flex flex-col gap-2">
              <span class="text-sm font-bold text-secondary-text uppercase tracking-wider">{{
                config.texts.previewAiCascading }}</span>
              <div class="flex flex-wrap gap-2">
                @for (rel of deleteCfg.cascadingRelations; track rel) { <nz-tag
                        class="!m-0 text-sm py-0.5 px-3 bg-alt-background border-secondary-border text-primary-text">{{
                  rel }}</nz-tag> }
              </div>
            </div>
            }

            @if (deleteCfg.restrictedStates) {
            <div class="flex flex-col gap-2 mt-2">
              <span class="text-sm font-bold text-secondary-text uppercase tracking-wider">{{
                config.texts.previewAiRestrictedStates }}</span>
              <div class="flex flex-col gap-3">
                @for (state of deleteCfg.restrictedStates; track state.description) {
                <div class="bg-alt-background border border-secondary-border p-4 rounded-lg flex flex-col gap-2">
                  <span class="text-sm font-medium text-primary-text">{{ state.description }}</span>
                  <span
                        class="text-xs font-mono text-secondary-text bg-component-inner p-2 rounded border border-primary-border">{{
                    state.where | json }}</span>
                </div>
                }
              </div>
            </div>
            }
          </div>
          }

        </div>
      </div>
      }

      @if (graphDataMap().get(model.name); as gData) {
      @if (gData.nodes.length > 1) {
      <div class="bg-section-background flex flex-col border-t border-secondary-border">
        <div class="px-6 py-3 border-b border-secondary-border bg-component-header flex items-center justify-between">
          <h4 class="text-sm font-bold text-secondary-text uppercase tracking-wider"><i
               class="fad fa-project-diagram mr-2"></i>{{ config.texts.previewLocalDiagramTitle }}</h4>
        </div>
        <rapider-data-models-diagram [heightPx]="350"
                                     [graphData]="gData"></rapider-data-models-diagram>
      </div>
      }
      }

      @if (model.titleFieldTemplate) {
      <div class="p-6 border-t border-secondary-border flex flex-col gap-2 bg-component-inner rounded-b-xl">
        <h4 class="text-sm font-bold text-secondary-text uppercase tracking-wider">{{ config.texts.previewTitleTemplate
          }}</h4>
        <div
             class="font-mono text-sm text-primary-text bg-alt-background border border-secondary-border px-4 py-2 rounded-md w-max shadow-sm">
          {{ model.titleFieldTemplate }}
        </div>
      </div>
      }

      }

    </div>
    }
  </section>

  @if (globalGraphData().nodes.length > 0) {
  <section
           class="bg-component-inner border border-primary-border rounded-xl shadow-sm overflow-hidden flex flex-col mt-4 min-h-[800px]">
    <div class="p-6 border-b border-secondary-border bg-component-header">
      <h2 class="text-xl font-bold text-heading"><i class="fad fa-project-diagram mr-2 text-primary"></i>{{
        config.texts.previewGlobalDiagramTitle }}</h2>
      <p class="text-sm text-secondary-text mt-1">{{ config.texts.previewGlobalDiagramSubtitle }}</p>
    </div>
    <rapider-data-models-diagram [heightPx]="800"
                                 [graphData]="globalGraphData()"></rapider-data-models-diagram>
  </section>
  }

  }
</div>

<!-- <nz-modal [nzVisible]="isConfirmModalVisible()"
            [nzWidth]="900"
            [nzTitle]="config.texts.confirmModalTitle"
            (nzOnCancel)="closeConfirmationModal()"
            (nzVisibleChange)="isConfirmModalVisible.set($event)"
            [nzFooter]="null">
    <ng-container *nzModalContent>
      <rapider-project-model-library-integration [templateData]="templateData()"
                                                 [currentModels]="currentProjectModels()"
                                                 (onCancel)="closeConfirmationModal()"
                                                 (onConfirm)="handleIntegrationConfirm($event)">
      </rapider-project-model-library-integration>
    </ng-container>
  </nz-modal>

  }
</div> -->